import React from 'react';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import Head from 'next/head';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import RemoveLeakedOnlyfansContentNew from '@/components/RemoveLeakedOnlyfansContentNew';
import { getTdkApi } from '@/lib/service/tdk';
import { getPageContentContentApi } from '@/lib/service/home';
import { faqJsonLd, page2JsonLd } from '@/lib/common/jsonld';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';
import { getFaqList } from '@/utils/landingData';
import { hyphenatedToTitleCase } from '@/shared/path-helper';

// ⭐ ISR: 转换为静态生成 + 7 天重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;
  const resolvedUrl = '/remove-leaked-onlyfans-content';
  const [localeRes, tdkData, res] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl, locale),
    getPageContentContentApi(`/remove-leaked-onlyfans-content`, locale, 0, ''),
  ]);
  let currentURL = `${process.env.NEXT_APP_WEB_HOST}/remove-leaked-onlyfans-content`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}/remove-leaked-onlyfans-content`;
  }
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  // ⭐ ISR Fallback: 如果当前语言内容不存在，回退到英文
  if (res.code != 0) {
    // 尝试获取英文版本（默认语言）
    if (locale !== 'en') {
      const enRes = await getPageContentContentApi(`/remove-leaked-onlyfans-content`, 'en', 0, '');
      if (enRes.code === 0 && enRes.data) {
        // 使用英文内容作为回退
        const siteUrl =
          locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
        const pageTitle = tdkData?.data?.title ?? tempData['head_RemoveFakeAccount_Title'];
        const pageDescription = tdkData?.data?.description ?? tempData['head_RemoveFakeAccount_Desc'];

        const jsonLd = page2JsonLd(
          {
            title: pageTitle,
            desc: pageDescription,
            url: currentURL,
          },
          { url: siteUrl },
        );

        const faqList = getFaqList(enRes);

        const faqJSONLD = faqJsonLd(
          {
            title: hyphenatedToTitleCase(resolvedUrl),
            desc: pageDescription,
            url: currentURL,
          },
          {
            url: siteUrl,
          },
          faqList,
        );

        return {
          props: {
            ...localeRes,
            herf: currentURL,
            data: enRes.data, // ✅ 使用英文内容
            title: pageTitle,
            description: pageDescription,
            jsonLd: jsonLd,
            faqJSONLd: faqJSONLD,
            // ✅ 标记这是回退内容
            isFallbackContent: true,
            actualLocale: 'en',
            requestedLocale: locale,
          },
          revalidate: ISR_REVALIDATE_TIME,
        };
      }
    }

    // 如果英文版本也不存在，返回 notFound
    return {
      notFound: true,
      revalidate: ISR_REVALIDATE_TIME,
    };
  }
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;

  const pageTitle = tdkData?.data?.title ?? tempData['head_RemoveFakeAccount_Title'];
  const pageDescription = tdkData?.data?.description ?? tempData['head_RemoveFakeAccount_Desc'];

  const jsonLd = page2JsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    { url: siteUrl },
  );

  const faqList = getFaqList(res);
  const faqJSONLD = faqJsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDescription,
      url: currentURL,
    },
    {
      url: siteUrl,
    },
    faqList,
  );

  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      data: res.data,
      title: pageTitle,
      description: pageDescription,
      jsonLd: jsonLd,
      faqJSONLd: faqJSONLD,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const PrivacyPolicyPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="Remove Leaked OnlyFans Content"
        additionalInfo={{
          语言: props.locale,
        }}
      />
      <RemoveLeakedOnlyfansContentNew data={props.data} />
    </>
  );
};

PrivacyPolicyPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const description = page?.props?.description;
  const jsonLd = page?.props?.jsonLd;
  const faqJSONLd = page?.props?.faqJSONLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
          {faqJSONLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJSONLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PrivacyPolicyPage;
